import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Search, 
  Trash2,
  Mail,
  Phone,
  Calendar,
  LogOut,
  LayoutDashboard
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminContacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setContacts(response.data.contacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Möchten Sie diesen Kontakt wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/contacts/${contactId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setContacts(contacts.filter(c => c.id !== contactId));
      setSelectedContact(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Fehler beim Löschen des Kontakts');
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_a2b-facility/artifacts/4frauqel_AtoBObjektservice.png" 
                alt="AtoB Logo" 
                className="h-10 w-auto"
              />
              <h1 className="text-2xl font-bold text-atob-blue">Kontaktanfragen</h1>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="text-red-600 border-red-600 hover:bg-red-50"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Abmelden
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-atob-blue text-white">
        <div className="container mx-auto px-4">
          <nav className="flex space-x-8 py-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <LayoutDashboard className="h-5 w-5" />
              <span>Dashboard</span>
            </button>
            <button
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-white/20 font-semibold"
            >
              <MessageSquare className="h-5 w-5" />
              <span>Kontakte</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contacts List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Alle Kontaktanfragen ({filteredContacts.length})</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-atob-orange"></div>
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Keine Kontakte gefunden</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredContacts.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => setSelectedContact(contact)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedContact?.id === contact.id
                            ? 'border-atob-orange bg-atob-orange/5'
                            : 'border-gray-200 hover:border-atob-orange/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-atob-blue mb-1">
                              {contact.name}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">
                              {contact.email} • {contact.phone}
                            </p>
                            <p className="text-sm text-gray-700 line-clamp-2">
                              {contact.message}
                            </p>
                            {contact.serviceType && (
                              <span className="inline-block mt-2 px-2 py-1 bg-atob-blue/10 text-atob-blue text-xs rounded">
                                {contact.serviceType}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(contact.timestamp).toLocaleDateString('de-DE')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contact Details */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedContact ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Name</label>
                      <p className="text-lg font-medium text-atob-blue">{selectedContact.name}</p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600 flex items-center">
                        <Mail className="h-4 w-4 mr-1" /> E-Mail
                      </label>
                      <a href={`mailto:${selectedContact.email}`} className="text-atob-orange hover:underline">
                        {selectedContact.email}
                      </a>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600 flex items-center">
                        <Phone className="h-4 w-4 mr-1" /> Telefon
                      </label>
                      <a href={`tel:${selectedContact.phone}`} className="text-atob-orange hover:underline">
                        {selectedContact.phone}
                      </a>
                    </div>

                    {selectedContact.serviceType && (
                      <div>
                        <label className="text-sm font-semibold text-gray-600">Service</label>
                        <p className="capitalize">{selectedContact.serviceType}</p>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Nachricht</label>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedContact.message}</p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Eingegangen am</label>
                      <p>{new Date(selectedContact.timestamp).toLocaleString('de-DE')}</p>
                    </div>

                    <div className="pt-4 border-t space-y-2">
                      <Button
                        onClick={() => window.open(`mailto:${selectedContact.email}`)}
                        className="w-full bg-atob-blue hover:bg-atob-blue/90"
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        E-Mail schreiben
                      </Button>
                      <Button
                        onClick={() => handleDelete(selectedContact.id)}
                        variant="outline"
                        className="w-full text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Löschen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Wählen Sie einen Kontakt aus</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
